import Action from '@/common/action'
import SimulateTouch from '@/shared/misc/simulate-touch'
import globalRenderer from '@/shared/misc/global-renderer'
import type { ISwipeArea, ITouch, Vector } from '@/shared/di/interface/touch'
import { container } from 'tsyringe'
import { ISize } from '../di/interface/size'
import { Interface } from '../di/interface'

const SWIPE_THRESHOLD = 30
const USE_DEBUG = false
export interface ExtendedTouch extends Touch {
  startTime: number
}
export class TouchManager implements ITouch {
  #element: HTMLCanvasElement = {} as HTMLCanvasElement
  #debugCanvas: HTMLCanvasElement = {} as HTMLCanvasElement
  #debugCtx: CanvasRenderingContext2D
  activeTouches: ExtendedTouch[] = []
  #timeStartedFirstTouch = 0
  #firstTouch: Vector = { x: 0, y: 0 }
  #longPressTimeout: number | undefined
  #swipeAreas: ISwipeArea[] = []
  onTouchesChanged: Action<Touch[]> = new Action()
  onTap: Action<MouseEvent> = new Action()
  onLongPress: Action<MouseEvent> = new Action()

  constructor() {
    this.#element = globalRenderer.el

    this.#debugCanvas = document.createElement('canvas') as HTMLCanvasElement
    this.#debugCanvas.style.position = 'absolute'
    this.#debugCanvas.style.left = '0px'
    this.#debugCanvas.style.top = '0px'
    this.#debugCanvas.style.zIndex = '10000'
    this.#debugCanvas.style.pointerEvents = 'none'

    if (USE_DEBUG) {
      document.body.appendChild(this.#debugCanvas)
    }

    this.#debugCtx = this.#debugCanvas.getContext('2d')!

    this.updateDebugCanvas = this.updateDebugCanvas.bind(this)
    const size = container.resolve<ISize>(Interface.ISize)
    size.onResize.add(this.updateDebugCanvas)

    this.#element.addEventListener('touchstart', (e: TouchEvent) => this.updateTouches(e))
    this.#element.addEventListener('touchmove', (e: TouchEvent) => this.updateTouches(e))
    this.#element.addEventListener('touchend', (e: TouchEvent) => this.updateTouches(e))

    SimulateTouch.simulate(this.#element)
  }

  updateDebugCanvas() {
    if (!USE_DEBUG) {
      return
    }
    const size = container.resolve<ISize>(Interface.ISize)
    const w = size.getSize().width
    const h = size.getSize().height
    this.#debugCanvas.width = w
    this.#debugCanvas.height = h

    this.#debugCtx.clearRect(0, 0, w, h)

    this.#swipeAreas.forEach(area => {
      this.#debugCtx.fillStyle = 'rgba(255,0,0,0.2)'
      this.#debugCtx.fillRect(
        area.originPercentageRect.x * w,
        area.originPercentageRect.y * h,
        area.originPercentageRect.x + area.originPercentageRect.width * w,
        area.originPercentageRect.y + area.originPercentageRect.height * h
      )

      if (area.startedAt) {
        this.#debugCtx.beginPath()
        this.#debugCtx.fillStyle = 'rgba(255,0,0,0.6)'
        this.#debugCtx.arc(area.startedAt.x * w, area.startedAt.y * h, 10, 0, 2 * Math.PI, false)
        this.#debugCtx.fill()
        this.#debugCtx.closePath()
      }
    })
  }

  private updateTouches(e: TouchEvent) {
    if (e.cancelable) {
      e.preventDefault()
    }

    const size = container.resolve<ISize>(Interface.ISize)

    // store first touch point for testing swipe and tap
    if (e.touches.length === 1 && this.activeTouches.length === 0) {
      this.#firstTouch.x = e.touches[0].clientX
      this.#firstTouch.y = e.touches[0].clientY

      this.#timeStartedFirstTouch = Date.now()

      //hitTest swipeArea

      this.#swipeAreas.forEach(area => {
        const percX = this.#firstTouch.x / size.getSize().width
        const percY = this.#firstTouch.y / size.getSize().height

        if (
          percX > area.originPercentageRect.x &&
          percX < area.originPercentageRect.x + area.originPercentageRect.width &&
          percY > area.originPercentageRect.y &&
          percY < area.originPercentageRect.y + area.originPercentageRect.height
        ) {
          area.startedAt = { x: percX, y: percY }
        } else {
          area.startedAt = undefined
        }
      })

      this.clearLongPressTimeout()
      this.#longPressTimeout = window.setTimeout(() => {
        if (this.activeTouches.length === 0) return

        if (
          Math.abs(this.#firstTouch.x - this.activeTouches[0].clientX) < 15 &&
          Math.abs(this.#firstTouch.y - this.activeTouches[0].clientY) < 15
        ) {
          const evt = new MouseEvent('mousedown', {
            clientX: this.activeTouches[0].clientX,
            clientY: this.activeTouches[0].clientY
          })
          this.onLongPress.invoke(evt)
        }
      }, 1000)
    }

    //enable next swipe
    if (e.touches.length === 0) {
      this.#swipeAreas.forEach(area => {
        area.isWaitingForSwipeRelease = false
      })
    }

    this.checkForTap(e)

    this.checkForSwipeArea(e)

    const realAndSimulatedTouches: ExtendedTouch[] = []

    if (e.touches.item) {
      for (let t = 0; t < e.touches.length; t++) {
        const touch: ExtendedTouch | null = e.touches.item(t) as ExtendedTouch
        if (touch) {
          realAndSimulatedTouches.push(touch)
        }
      }
    } else {
      //simulated touch events from misc/simulate-touch.js
      for (let t = 0; t < e.touches.length; t++) {
        const touch: ExtendedTouch | null = e.touches[t] as ExtendedTouch
        if (touch) {
          realAndSimulatedTouches.push(touch)
        }
      }
    }

    // update and add touches to active touches
    realAndSimulatedTouches.forEach(touch => {
      const i = this.activeTouches.findIndex(t => t.identifier === touch?.identifier)
      if (i === -1) {
        touch.startTime = Date.now()
        this.activeTouches.push(touch)
      } else {
        const startTime = this.activeTouches[i].startTime
        this.activeTouches[i] = touch
        this.activeTouches[i].startTime = startTime
      }
    })

    // remove touches from active touches
    for (let index = this.activeTouches.length - 1; index >= 0; index--) {
      if (!realAndSimulatedTouches.find(t => t.identifier === this.activeTouches[index].identifier)) {
        this.activeTouches.splice(index, 1)
      }
    }

    this.activeTouches.sort((a, b) => {
      return a.startTime - b.startTime
    })

    this.onTouchesChanged.invoke(this.activeTouches)

    this.updateDebugCanvas()
  }

  private clearLongPressTimeout() {
    if (this.#longPressTimeout) {
      window.clearTimeout(this.#longPressTimeout)
      this.#longPressTimeout = undefined
    }
  }

  private checkForTap(e: TouchEvent) {
    if (e.touches.length === 0 && this.activeTouches.length === 1 && Date.now() - this.#timeStartedFirstTouch < 300) {
      const evt = new MouseEvent('mousedown', { clientX: this.#firstTouch.x, clientY: this.#firstTouch.y })
      this.onTap.invoke(evt)
    }
  }

  private checkForSwipeArea(e: TouchEvent): boolean {
    if (e.touches.length !== 1) {
      return false
    }

    this.#swipeAreas.forEach(area => {
      if (area.startedAt === undefined || area.isWaitingForSwipeRelease) {
        return
      }

      const size = container.resolve<ISize>(Interface.ISize)
      const touchX = e.touches[0].clientX / size.getSize().width
      const touchY = e.touches[0].clientY / size.getSize().height
      const xDiff = area.startedAt.x - touchX
      const yDiff = area.startedAt.y - touchY
      const dist = Math.sqrt(xDiff * xDiff + yDiff * yDiff)

      const angle = Math.atan2(-yDiff, -xDiff)

      //distande in radians to preferred angle
      const angleDiff = Math.abs(((Math.abs(angle - area.angle) + Math.PI) % (2 * Math.PI)) - Math.PI)

      if (dist > area.distance && angleDiff < area.angleMargin) {
        if (area.callback) {
          area.callback()
          area.isWaitingForSwipeRelease = true
        }
      }
    })

    return false
  }

  registerSwipeArea(config: ISwipeArea) {
    if (!this.#swipeAreas.find(area => area.id === config.id)) {
      this.#swipeAreas.push(config)
    }
    this.updateDebugCanvas()
  }

  unregisterSwipeArea(id: string) {
    const index = this.#swipeAreas.findIndex(area => area.id === id)
    if (index > -1) {
      this.#swipeAreas.splice(index, 1)
    }
    this.updateDebugCanvas()
  }

  unregisterAllSwipeAreas() {
    this.#swipeAreas.length = 0
    this.updateDebugCanvas()
  }

  get anyTouches(): boolean {
    return this.activeTouches.length > 0
  }

  isTouchActive(identifier: number): boolean {
    return this.activeTouches.findIndex(touch => touch.identifier === identifier) !== -1
  }
}
